/**
 * StripeOrdersPanel — Dashboard Stripe réutilisable pour les plugins commerce.
 * Affiche les transactions Stripe d'un shopId/pluginType donné, permet le remboursement.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { fetchAPI } from '../services/api';

interface StripeTransaction {
  id: string;
  stripeId: string;
  chargeId?: string;
  status: string;
  amount: number;
  currency: string;
  customerEmail?: string;
  customerName?: string;
  description?: string;
  pluginType?: string;
  shopId?: string;
  refundedAt?: string;
  refundAmount?: number;
  stripeDate: string;
}

interface Summary {
  totalRevenue: number;
  totalTransactions: number;
  totalRefunded: number;
  totalRefundCount: number;
}

interface Props {
  shopId?: string;
  pluginType?: string;
  currency?: string;
}

const fmt = (amount: number, currency = 'EUR') =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount);

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const statusBadge = (status: string) => {
  const map: Record<string, { color: string; label: string }> = {
    succeeded: { color: '#22c55e', label: 'Réussi' },
    refunded:  { color: '#f59e0b', label: 'Remboursé' },
    pending:   { color: '#94a3b8', label: 'En attente' },
    failed:    { color: '#ef4444', label: 'Échoué' },
  };
  const s = map[status] || { color: '#94a3b8', label: status };
  return (
    <span style={{ background: s.color + '22', color: s.color, border: `1px solid ${s.color}44`, borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
      {s.label}
    </span>
  );
};

const StripeOrdersPanel: React.FC<Props> = ({ shopId, pluginType, currency = 'EUR' }) => {
  const [transactions, setTransactions] = useState<StripeTransaction[]>([]);
  const [summary, setSummary]           = useState<Summary | null>(null);
  const [loading, setLoading]           = useState(false);
  const [syncing, setSyncing]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [refundingId, setRefundingId]   = useState<string | null>(null);
  const [page, setPage]                 = useState(1);
  const [total, setTotal]               = useState(0);
  const LIMIT = 20;

  const buildParams = useCallback(() => {
    const p: Record<string, string> = { page: String(page), limit: String(LIMIT) };
    if (shopId)     p.shopId     = shopId;
    if (pluginType) p.pluginType = pluginType;
    return new URLSearchParams(p).toString();
  }, [shopId, pluginType, page]);

  const buildSummaryParams = useCallback(() => {
    const p: Record<string, string> = {};
    if (shopId)     p.shopId     = shopId;
    if (pluginType) p.pluginType = pluginType;
    return new URLSearchParams(p).toString();
  }, [shopId, pluginType]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [txResp, sumResp]: any[] = await Promise.all([
        fetchAPI(`/stripe-dashboard/transactions?${buildParams()}`),
        fetchAPI(`/stripe-dashboard/summary?${buildSummaryParams()}`),
      ]);
      setTransactions(txResp.transactions || []);
      setTotal(txResp.total || 0);
      setSummary(sumResp.summary || null);
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [buildParams, buildSummaryParams]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const r: any = await fetchAPI('/stripe-dashboard/sync', { method: 'POST' });
      await load();
      if (r.synced === 0) setError('Aucune nouvelle transaction trouvée (30 derniers jours).');
    } catch (e: any) {
      setError(e?.message || 'Erreur synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleRefund = async (tx: StripeTransaction) => {
    if (!window.confirm(`Rembourser ${fmt(tx.amount, tx.currency)} à ${tx.customerEmail || tx.customerName || 'ce client'} ?`)) return;
    setRefundingId(tx.stripeId);
    setError(null);
    try {
      await fetchAPI('/stripe-dashboard/refund', {
        method: 'POST',
        body: JSON.stringify({ transactionId: tx.stripeId }),
      });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur remboursement');
    } finally {
      setRefundingId(null);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* ── Résumé ─────────────────────────────────────────────────────────── */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Revenus Stripe', value: fmt(summary.totalRevenue, currency), color: '#635bff' },
            { label: 'Transactions',   value: summary.totalTransactions,            color: '#3b82f6' },
            { label: 'Remboursé',      value: fmt(summary.totalRefunded, currency), color: '#f59e0b' },
            { label: 'Nb remboursements', value: summary.totalRefundCount,          color: '#94a3b8' },
          ].map(c => (
            <div key={c.label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{c.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Actions ──────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <button
          onClick={handleSync}
          disabled={syncing}
          style={{ background: '#635bff', color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', fontWeight: 600, cursor: syncing ? 'not-allowed' : 'pointer', opacity: syncing ? 0.7 : 1, fontSize: 13 }}
        >
          {syncing ? '⏳ Synchronisation…' : '🔄 Sync depuis Stripe'}
        </button>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>
          Importe les 30 derniers jours depuis l'API Stripe
        </span>
        <button onClick={load} disabled={loading} style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}>
          ↺ Actualiser
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 12px', color: '#dc2626', fontSize: 13, marginBottom: 10 }}>
          {error}
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Chargement…</div>
      ) : transactions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>
          Aucune transaction Stripe.<br />
          <span style={{ fontSize: 12 }}>Cliquez sur "Sync depuis Stripe" pour importer vos transactions.</span>
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  {['Date', 'Statut', 'Montant', 'Client', 'Description', 'Action'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transactions.map(tx => (
                  <tr key={tx.stripeId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 10px', color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(tx.stripeDate)}</td>
                    <td style={{ padding: '8px 10px' }}>{statusBadge(tx.status)}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 600, color: tx.status === 'refunded' ? '#f59e0b' : '#1e293b' }}>
                      {fmt(tx.amount, tx.currency)}
                      {tx.refundAmount && tx.refundAmount > 0 && (
                        <div style={{ fontSize: 10, color: '#f59e0b' }}>−{fmt(tx.refundAmount, tx.currency)}</div>
                      )}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ fontSize: 12 }}>{tx.customerName || '—'}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>{tx.customerEmail || ''}</div>
                    </td>
                    <td style={{ padding: '8px 10px', color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.description || '—'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      {tx.status === 'succeeded' && (
                        <button
                          onClick={() => handleRefund(tx)}
                          disabled={refundingId === tx.stripeId}
                          style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                        >
                          {refundingId === tx.stripeId ? '…' : 'Rembourser'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 12 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', background: 'white' }}>‹</button>
              <span style={{ padding: '4px 10px', fontSize: 13 }}>Page {page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', background: 'white' }}>›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StripeOrdersPanel;
